import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createBlock as _createBlock, resolveComponent as _resolveComponent } from "vue"

const _hoisted_1 = { class: "project-notes" }
const _hoisted_2 = { class: "notes-header" }
const _hoisted_3 = { class: "header-actions" }
const _hoisted_4 = ["disabled"]
const _hoisted_5 = {
  key: 0,
  class: "loading-indicator"
}
const _hoisted_6 = {
  key: 1,
  class: "error-message"
}
const _hoisted_7 = {
  key: 2,
  class: "empty-state"
}
const _hoisted_8 = {
  key: 3,
  class: "grid-content"
}

import { ref, onMounted, watch, computed, Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import notesService from '@/services/notes.service';
import AppDataGrid from '@/components/base/AppDataGrid.vue';
import NoteModal from './NoteModal.vue';
import { useRouter } from 'vue-router'
import ConfirmModal from '@/components/common/ConfirmModal.vue';
import { useStore } from '@/composables/useStore';

interface Note {
  id?: string | number;
  title?: string;
  content?: string;
  createdAt?: string;
  createdBy?: any;
  createdByName?: string;
  priority?: string;
  [key: string]: any;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ProjectNotes',
  props: {
    projectId: {}
  },
  emits: ["return-to-project"],
  setup(__props: any, { emit: __emit }) {

// Follow repo pattern to avoid noisy Vue import typing in some environments
// @ts-ignore
const props = __props;
const emit = __emit;

const { t } = useI18n();
const store = useStore();

// Reactive state
const notes = ref<Note[]>([]);
const loading = ref(false);
const error = ref('');
// Modals
const showNoteModal = ref(false);
const showDeleteModal = ref(false);
const editingNote = ref<Note | null>(null);
const noteToDelete = ref<Note | null>(null);

const router = useRouter()

// AG Grid configuration - make reactive to language changes
const columnDefs = computed(() => [
  {
    field: 'title',
    headerName: t('notes.title'),
    type: 'text',
    flex: 2,
    minWidth: 200,
    sortable: true,
    resizable: true
  },
  {
    field: 'createdAt',
    headerName: t('notes.creationDate'),
    type: 'timestamp',
    width: 150,
    sortable: true,
    resizable: true
  },
  {
    field: 'createdByName',
    headerName: t('notes.createdBy'),
    type: 'text',
    width: 150,
    sortable: true,
    resizable: true,
    cellRenderer: (params: any) => {
      return params.value || t('notes.unknownUser');
    }
  },
  {
    field: 'priority',
    headerName: t('notes.priority.label'),
    type: 'text',
    width: 120,
    sortable: true,
    resizable: true,
    cellRenderer: (params: any) => {
      const priority = params.value as string | undefined;
      if (!priority) return '';

      const priorityClass = `priority-${priority.toLowerCase()}`;
      const priorityText = t(`notes.priority.${priority.toLowerCase()}`);

      return `<span class="priority-badge ${priorityClass}">${priorityText}</span>`;
    }
  }
]);

// Actions configuration for AppDataGrid - make reactive to language changes
const actions = computed(() => [
  {
    name: 'discuss',
    icon: 'fas fa-comments',
    label: t('notes.discuss'),
    tooltip: t('notes.discuss'),
    class: 'discuss-btn'
  },
  {
    name: 'edit',
    icon: 'fas fa-edit',
    label: t('common.edit'),
    tooltip: t('common.edit'),
    class: 'edit-btn',
    condition: (data: any) => {
      const user = store.getters.getUser.value as any;
      try {
        if (!user) return false;
        const uid = String(user.id || '');
        return data?.createdBy && String(data.createdBy) === uid;
      } catch (e) {
        return false;
      }
    }
  },
  {
    name: 'delete',
    icon: 'fas fa-trash',
    label: t('common.delete'),
    tooltip: t('common.delete'),
    class: 'delete-btn',
    condition: (data: any) => {
      const user = store.getters.getUser.value as any;
      try {
        if (!user) return false;
        const uid = String(user.id || '');
        return data?.createdBy && String(data.createdBy) === uid;
      } catch (e) {
        return false;
      }
    }
  }
]);

const defaultColDef = ref({
  sortable: true,
  resizable: true,
  cellStyle: {
    display: 'flex',
    alignItems: 'center',
    lineHeight: '1.4'
  }
});

// Fetch notes
const fetchNotes = async () => {
  try {
    loading.value = true;
    error.value = '';

  const response = await notesService.getAllProjectNotes(props.projectId);
  // Some service calls return a wrapper object (e.g. { content: [...] }) — be tolerant
  notes.value = ((response as unknown) as Note[]) || [];

  } catch (err: any) {
    console.error('Error fetching notes:', err);
    error.value = err.message || t('notes.errorFetching');
  } finally {
    loading.value = false;
  }
};

// Note operations
const openCreateNoteModal = () => {
  editingNote.value = null;
  showNoteModal.value = true;
};

const editNote = (note: Note | any) => {
  if (!note) {
    console.error('ProjectNotes: Cannot edit note - note is undefined or null');
    return;
  }

  editingNote.value = { ...note } as Note;
  showNoteModal.value = true;
};

const closeNoteModal = () => {
  showNoteModal.value = false;
  editingNote.value = null;
};

const handleNoteSave = async (noteData: any) => {
  try {
    if (editingNote.value) {
      // Update existing note
      await notesService.updateNote(props.projectId, String(editingNote.value.id), noteData);
    } else {
      // Create new note
      await notesService.createNote(props.projectId, noteData);
    }
    closeNoteModal();

    fetchNotes();

  } catch (err) {
    console.error('Error saving note:', err);
    // Modal will handle error display
    throw err;
  }
};

const deleteNote = (note: Note | any) => {
  noteToDelete.value = note;
  showDeleteModal.value = true;
};

const closeDeleteModal = () => {
  showDeleteModal.value = false;
  noteToDelete.value = null;
};

const openDiscussionModal = (note: Note | any) => {
  // Navigate to the discussion page for the note instead of opening a modal
  if (!note || !note.id) return
  router.push({ name: 'NoteDiscussion', params: { projectId: String(props.projectId), noteId: String(note.id) } })
}

const handleDeleteConfirm = async () => {
  try {
    await notesService.deleteNote(props.projectId, String(noteToDelete.value?.id));
    closeDeleteModal();

    fetchNotes();

  } catch (err: any) {
    console.error('Error deleting note:', err);
    error.value = err.message || t('notes.errorDeleting');
    closeDeleteModal();
  }
};

// AG Grid event handlers
const onGridReady = () => {
  // Grid is ready
};

const handleAction = (action: any, row: any) => {
  switch (action.name) {
    case 'discuss':
      openDiscussionModal(row);
      break;
    case 'edit':
      editNote(row);
      break;
    case 'delete':
      deleteNote(row);
      break;
    default:
      console.warn('Unknown action:', action.name);
  }
};

// Watch for projectId changes
watch(() => props.projectId, () => {
  if (props.projectId) {
    fetchNotes();
  }
});

// Initial load
onMounted(() => {
  if (props.projectId) {
    fetchNotes();
  }
});

return (_ctx: any,_cache: any) => {
  const _component_NoteDiscussionModal = _resolveComponent("NoteDiscussionModal")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _cache[1] || (_cache[1] = _createElementVNode("h2", { class: "component-title" }, null, -1)),
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("button", {
          class: "btn btn-primary",
          onClick: openCreateNoteModal,
          disabled: loading.value
        }, [
          _cache[0] || (_cache[0] = _createElementVNode("span", { class: "btn-icon" }, "+", -1)),
          _createTextVNode(" " + _toDisplayString(_ctx.$t('notes.addNote')), 1)
        ], 8, _hoisted_4)
      ])
    ]),
    (loading.value && notes.value.length === 0)
      ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
          _cache[2] || (_cache[2] = _createElementVNode("div", { class: "spinner" }, null, -1)),
          _createElementVNode("p", null, _toDisplayString(_ctx.$t('common.loading')), 1)
        ]))
      : (error.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
            _createElementVNode("p", null, _toDisplayString(error.value), 1),
            _createElementVNode("button", {
              onClick: fetchNotes,
              class: "btn btn-secondary"
            }, _toDisplayString(_ctx.$t('common.retry')), 1)
          ]))
        : (notes.value.length === 0 && !loading.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
              _cache[3] || (_cache[3] = _createElementVNode("svg", {
                viewBox: "0 0 24 24",
                width: "60",
                height: "60"
              }, [
                _createElementVNode("path", {
                  fill: "var(--neutral-300)",
                  d: "M19,3H4.99C3.89,3 3,3.9 3,5L3,19C3,20.1 3.89,21 4.99,21H19C20.1,21 21,20.1 21,19V5C21,3.9 20.1,3 19,3M19,19H4.99V5H19V19M5,16H19V19H5V16M5,7H19V10H5V7Z"
                })
              ], -1)),
              _createElementVNode("p", null, _toDisplayString(_ctx.$t('notes.noNotesAvailable')), 1),
              _createElementVNode("button", {
                class: "btn btn-primary",
                onClick: openCreateNoteModal
              }, _toDisplayString(_ctx.$t('notes.addFirstNote')), 1)
            ]))
          : (_openBlock(), _createElementBlock("div", _hoisted_8, [
              _createVNode(AppDataGrid, {
                columnDefs: columnDefs.value,
                rowData: notes.value,
                defaultColDef: defaultColDef.value,
                pageSizeOptions: [10, 20, 50],
                pagination: true,
                pageSize: 20,
                suppressPaginationPanel: false,
                animateRows: true,
                showSearch: true,
                searchPlaceholder: _ctx.$t('notes.searchPlaceholder'),
                onGridReady: onGridReady,
                onActionClicked: handleAction,
                actions: actions.value,
                gridStyle: "width: 100%;",
                domLayout: "autoHeight"
              }, null, 8, ["columnDefs", "rowData", "defaultColDef", "searchPlaceholder", "actions"])
            ])),
    (showNoteModal.value)
      ? (_openBlock(), _createBlock(NoteModal, {
          key: 4,
          note: editingNote.value,
          "project-id": _ctx.projectId,
          onSave: handleNoteSave,
          onClose: closeNoteModal
        }, null, 8, ["note", "project-id"]))
      : _createCommentVNode("", true),
    (_ctx.showDiscussionModal)
      ? (_openBlock(), _createBlock(_component_NoteDiscussionModal, {
          key: 5,
          note: _ctx.discussingNote,
          "project-id": _ctx.projectId,
          onClose: _ctx.closeDiscussionModal
        }, null, 8, ["note", "project-id", "onClose"]))
      : _createCommentVNode("", true),
    _createVNode(ConfirmModal, {
      isVisible: showDeleteModal.value,
      title: _ctx.$t('notes.deleteConfirm.title'),
      message: _ctx.$t('notes.deleteConfirm.message', { title: noteToDelete.value?.title }),
      onConfirm: handleDeleteConfirm,
      onCancel: closeDeleteModal
    }, null, 8, ["isVisible", "title", "message"])
  ]))
}
}

})